"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect } from "react";
import { toast } from "sonner";
import type { Locale } from "@/i18n-config";

/**
 * Keeps the user inside the app while the connection is down.
 *
 * The service worker's navigation fallback (public/sw.js) serves /offline
 * whenever a document request fails, which meant a single click while offline
 * replaced the whole app with a dead-end "You're Offline" page. Next.js
 * client-side navigations are RSC fetches rather than document requests, so
 * they don't hit that fallback — they just hang or throw — but a hard
 * navigation (target=_blank, a full reload, an external link) does.
 *
 * Instead of letting either happen, this intercepts in-app link clicks while
 * `navigator.onLine` is false and surfaces a toast — the YouTube behaviour:
 * you stay where you are, you're told why, and the moment the connection is
 * back the app carries on (react-query refetches on reconnect).
 */
export default function OfflineNavigationGuard(t0) {
  const $ = _c(3);
  const {
    lang
  } = t0;
  let t1;
  let t2;
  if ($[0] !== lang) {
    t1 = () => {
      const isFa = lang === "fa";
      const handleClick = event => {
        if (navigator.onLine) {
          return;
        }
        if (event.defaultPrevented || event.button !== 0) {
          return;
        }
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
          return;
        }
        const anchor = (event.target as HTMLElement | null)?.closest("a");
        if (!anchor) {
          return;
        }
        const href = anchor.getAttribute("href");
        if (!href || href.startsWith("#") || anchor.hasAttribute("download")) {
          return;
        }
        const url = new URL(href, window.location.href);
        if (url.protocol !== "http:" && url.protocol !== "https:") {
          return;
        }
        if (url.href === window.location.href) {
          return;
        }
        event.preventDefault();
        toast.error(isFa ? "\u0627\u062A\u0635\u0627\u0644 \u0627\u06CC\u0646\u062A\u0631\u0646\u062A \u0628\u0631\u0642\u0631\u0627\u0631 \u0646\u06CC\u0633\u062A" : "You're offline", {
          id: "offline-navigation",
          description: isFa ? "\u067E\u0633 \u0627\u0632 \u0628\u0631\u0642\u0631\u0627\u0631\u06CC \u0627\u062A\u0635\u0627\u0644 \u062F\u0648\u0628\u0627\u0631\u0647 \u062A\u0644\u0627\u0634 \u06A9\u0646\u06CC\u062F." : "Reconnect and try again."
        });
      };
      document.addEventListener("click", handleClick, true);
      return () => document.removeEventListener("click", handleClick, true);
    };
    t2 = [lang];
    $[0] = lang;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  return null;
}